<script setup lang="ts">
import { ref, onBeforeUnmount, watch, nextTick } from 'vue'

const props = withDefaults(defineProps<{
  open: boolean
  title?: string
  maxWidth?: string
  closable?: boolean
}>(), {
  open: false,
  maxWidth: 'max-w-lg',
  closable: true,
})

const emit = defineEmits<{
  close: []
}>()

const panelRef = ref<HTMLElement | null>(null)
const previousFocus = ref<HTMLElement | null>(null)

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.closable) {
    emit('close')
  }
  if (e.key === 'Tab') {
    trapFocus(e)
  }
}

function trapFocus(e: KeyboardEvent) {
  const panel = panelRef.value
  if (!panel) return
  const focusable = panel.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )
  if (focusable.length === 0) return
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault()
    last.focus()
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault()
    first.focus()
  }
}

watch(() => props.open, async (val) => {
  if (val) {
    previousFocus.value = document.activeElement as HTMLElement
    await nextTick()
    const firstFocusable = panelRef.value?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    firstFocusable?.focus()
    document.addEventListener('keydown', onKeydown)
  } else {
    previousFocus.value?.focus()
    previousFocus.value = null
    document.removeEventListener('keydown', onKeydown)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="open"
        class="modal-overlay"
        @click.self="closable && emit('close')"
        role="dialog"
        aria-modal="true"
        :aria-label="title || 'Modal'"
      >
        <div
          ref="panelRef"
          :class="['modal-panel', props.maxWidth]"
          @click.stop
        >
          <div v-if="title || closable" class="modal-header">
            <h2 class="modal-title">{{ title }}</h2>
            <button
              v-if="closable"
              @click="emit('close')"
              class="btn-ghost -mr-2 h-8 w-8 rounded-lg p-0"
              aria-label="Fechar"
            >
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="modal-body">
            <slot />
          </div>
          <div v-if="$slots.footer" class="modal-footer">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active {
  transition: opacity 0.2s ease-out;
}
.modal-enter-active .modal-panel {
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s ease-out;
}
.modal-leave-active {
  transition: opacity 0.15s ease-in;
}
.modal-leave-active .modal-panel {
  transition: transform 0.15s ease-in, opacity 0.15s ease-in;
}
.modal-enter-from {
  opacity: 0;
}
.modal-enter-from .modal-panel {
  opacity: 0;
  transform: scale(0.95) translateY(8px);
}
.modal-leave-to {
  opacity: 0;
}
.modal-leave-to .modal-panel {
  opacity: 0;
  transform: scale(0.95);
}
</style>
